import QRCode from 'react-qr-code';
import { formatarReais, fraseSorteio, listaPremios, pad4 } from '@/pages/talao/utils';
import type { RifaImpressao, TalaoImpressao } from '../types';

interface Props {
  rifa: RifaImpressao;
  talao: TalaoImpressao;
  link: string;
}

function montarNumeros(inicio: number, fim: number): number[] {
  const lista: number[] = [];
  for (let n = inicio; n <= fim; n += 1) lista.push(n);
  return lista;
}

function TabelaControle({ numeros }: { numeros: number[] }) {
  return (
    <table className="w-full border-collapse text-[11px] leading-tight">
      <thead>
        <tr>
          <th className="border border-black px-1 py-0.5 text-left font-bold">Nº</th>
          <th className="border border-black px-2 py-0.5 text-left font-bold">Nome</th>
          <th className="border border-black px-2 py-0.5 text-left font-bold">WhatsApp</th>
        </tr>
      </thead>
      <tbody>
        {numeros.map((n) => (
          <tr key={n}>
            <td className="border border-black px-1 py-[3px] font-mono">{pad4(n)}</td>
            <td className="border border-black px-2 py-[3px]">&nbsp;</td>
            <td className="border border-black px-2 py-[3px]">&nbsp;</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GradeControle({ numeros }: { numeros: number[] }) {
  if (numeros.length <= 25) {
    return <TabelaControle numeros={numeros} />;
  }
  const meio = Math.ceil(numeros.length / 2);
  return (
    <div className="grid grid-cols-2 gap-4">
      <TabelaControle numeros={numeros.slice(0, meio)} />
      <TabelaControle numeros={numeros.slice(meio)} />
    </div>
  );
}

export default function FolhaTalao({ rifa, talao, link }: Props) {
  const numeros = montarNumeros(talao.inicio, talao.fim);
  const premiosTexto = listaPremios(rifa.premios);
  const frase = fraseSorteio(rifa);

  return (
    <div className="folha relative">
      {!talao.token_ativo && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <div className="rotate-[-18deg] border-[3px] border-red-600 px-10 py-2 text-5xl font-black uppercase tracking-[0.2em] text-red-600">
            Cancelado
          </div>
        </div>
      )}

      <div className="folha-interna">
        {/* Cabeçalho */}
        <header className="border-b-2 border-black pb-2">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-[22px] font-black uppercase leading-tight">
              Ação entre amigos {rifa.titulo}
            </h1>
            <span className="shrink-0 text-[22px] font-black">{formatarReais(rifa.valor_cota)}</span>
          </div>
          {premiosTexto && <p className="mt-1.5 text-[13px]">{premiosTexto}</p>}
          <p className="mt-1 text-[13px]">{frase}</p>
        </header>

        {/* Vendedor + QR */}
        <div className="mt-4 flex items-start justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide">Vendedor</p>
            <p className="mt-1 text-[15px] font-semibold">{talao.vendedor}</p>
            <p className="mt-5 text-[11px] font-bold uppercase tracking-wide">Números deste talão</p>
            <p className="mt-1 text-[15px] font-semibold">
              {pad4(talao.inicio)} a {pad4(talao.fim)}
            </p>
          </div>
          <div className="shrink-0 text-center">
            {talao.token_ativo ? (
              <>
                <div className="border-2 border-black p-2">
                  <QRCode value={link} size={170} level="M" />
                </div>
                <p className="mt-1 font-mono text-[13px] font-semibold tracking-[0.25em]">
                  {(talao.token || '').toUpperCase()}
                </p>
              </>
            ) : (
              <div className="h-[190px] w-[174px]" />
            )}
          </div>
        </div>

        {/* Instrução */}
        <div className="mt-4 rounded border-2 border-black px-4 py-2.5 text-center">
          <p className="text-[13px] font-bold uppercase">Aponte a câmera do celular no código</p>
          <p className="mt-0.5 text-[12px]">
            Escolha quantos números quer e pague no Pix. Os números saem na hora, no seu nome.
          </p>
        </div>

        {/* Controle do vendedor */}
        <div className="mt-5">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide">
            Controle do vendedor (anote aqui se quiser)
          </p>
          <GradeControle numeros={numeros} />
        </div>

        {/* Rodapé */}
        <footer className="mt-auto flex items-center justify-between border-t-2 border-black pt-2 text-[11px]">
          <span>Realização: {rifa.entidade_nome || ''}</span>
          <span>Se Liga Católico</span>
        </footer>
      </div>
    </div>
  );
}